import firebase from "firebase/app";
import "firebase/auth";
import "firebase/database";

/**
 * Firebase itself is initialized by <FirebaseDatabaseProvider> in index.js — a React
 * component, so initialization only happens at render time. Nothing in this module may call
 * into the SDK (firebase.database()/firebase.auth()) at import time as a result: every access
 * is deferred into a function, so it only ever runs when actually invoked — always well after
 * the provider has mounted, regardless of whether this file itself was imported eagerly or
 * lazily.
 */
function getDb() {
  return firebase.database()
}

// onAuthStateChanged normally fires within milliseconds. This bound exists
// because FirebaseSession holds the app behind a Loader until the session
// settles — if the SDK never reports state (blocked IndexedDB in private mode,
// a failed init) the app would hang on "Loading..." forever.
const AUTH_RESTORE_TIMEOUT_MS = 5000;

/**
 * Resolve with the persisted session, or null if none is restored in time.
 *
 * firebase.auth().currentUser is null immediately after init even when a
 * session exists, so checking it synchronously would sign in again on every
 * page load. onAuthStateChanged fires once restoration settles.
 */
const waitForAuthRestore = () =>
  new Promise((resolve) => {
    let settled = false;

    const finish = (user) => {
      if (settled) {
        return;
      }
      settled = true;
      clearTimeout(timer);
      try {
        unsubscribe();
      } catch (e) {
        // unsubscribe is not assigned yet if the callback fired synchronously
      }
      resolve(user);
    };

    const timer = setTimeout(() => {
      console.error('[Firebase] auth state restore timed out');
      finish(null);
    }, AUTH_RESTORE_TIMEOUT_MS);

    const unsubscribe = firebase.auth().onAuthStateChanged(finish);
  });

let sessionPromise = null;

/**
 * Establish the Firebase session for `uid`, exchanging the backend-minted
 * custom token only when needed.
 *
 * `uid` must be the ms-tgp user id (profile.id) — the same value the RTDB
 * security rules match against data/favorites/$uid and data/acl/$uid.
 *
 * Memoised so concurrent callers share one sign-in.
 */
export const signInToFirebase = (customToken, uid) => {
  if (!sessionPromise) {
    sessionPromise = (async () => {
      const restored = await waitForAuthRestore();

      // A persisted session for the RIGHT user is reused as-is.
      if (restored && (!uid || restored.uid === uid)) {
        return restored;
      }

      // A persisted session for a DIFFERENT user must be torn down first.
      // Logout did not previously clear the Firebase session, so a browser can
      // hold user A's session while user B is logged in — B would then read and
      // write A's favorites under the security rules.
      if (restored && uid && restored.uid !== uid) {
        console.warn(
          `[Firebase] stale session for ${restored.uid}, re-authenticating as ${uid}`,
        );
        await firebase.auth().signOut();
      }

      if (!customToken) {
        throw new Error('No Firebase custom token available');
      }

      const credential = await firebase
        .auth()
        .signInWithCustomToken(customToken);

      return credential.user;
    })().catch((err) => {
      sessionPromise = null;
      throw err;
    });
  }

  return sessionPromise;
};

/**
 * Tear down the Firebase session. Must run on logout: the session
 * auto-refreshes indefinitely and would otherwise outlive the TGP session in
 * an open tab, and would be reused by the next user to log in on this browser.
 */
export const signOutOfFirebase = async () => {
  sessionPromise = null;
  try {
    await firebase.auth().signOut();
  } catch (e) {
    console.error('Firebase sign-out failed:', e);
  }
};

export const saveFavorite = (orderId, userId) => {
  getDb().ref(`data/favorites/${userId}/${orderId}`).set("")
}

export const removeFavorite = (orderId, userId) => {
  getDb().ref(`data/favorites/${userId}/${orderId}`).remove()
}

export default getDb
